import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

const PAGE_PARAM = "page";

/**
 * Reads and writes the current page (1-based) in the URL query string.
 * Page 1 is omitted from the URL to keep it clean.
 * Use with paginated lists so the page is restored when navigating back.
 */
export function useListPageParam(): [number, (page: number) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageParam = searchParams.get(PAGE_PARAM);
  const page = (() => {
    if (pageParam == null || pageParam === "") return 1;
    const n = parseInt(pageParam, 10);
    return Number.isNaN(n) || n < 1 ? 1 : n;
  })();

  const setPage = useCallback(
    (nextPage: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (nextPage <= 1) {
            next.delete(PAGE_PARAM);
          } else {
            next.set(PAGE_PARAM, String(nextPage));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return [page, setPage];
}
